import React, { createContext, useContext, useEffect, useState } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '../supabase/client';
import { jwtDecode } from 'jwt-decode';

interface AuthMeta {
  fullName?: string;
}

interface AuthContextProps {
  user: User | null;
  permissions: string[] | null;
  meta: AuthMeta | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signUp: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: string | null }>;
  hasPermissions: (requiresPermission: string[], p?: string[]) => boolean;
  clearUserSession: () => void;
}

const AuthContext = createContext<AuthContextProps>({
  user: null,
  permissions: [],
  meta: null,
  session: null,
  loading: true,
  signIn: async () => ({ error: null }),
  signUp: async () => ({ error: null }),
  signOut: async () => {},
  resetPassword: async () => ({ error: null }),
  hasPermissions: () => false,
  clearUserSession: () => {},
});

function clearAllCookies() {
  const cookies = document.cookie.split(';');

  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i];
    const eqPos = cookie.indexOf('=');
    const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
    document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [meta, setMeta] = useState<AuthMeta | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState<string[] | null>(null);

  const clearUserSession = () => {
    setSession(null);
    setMeta(null);
    setUser(null);
    setPermissions([]);
    clearAllCookies();
  }

  useEffect(() => {
    // Check active session on load
    // supabase.auth.getSession().then(({ data }) => {
    //   setSession(data?.session);

    //   if (data?.session?.access_token) {
    //     const jwt = jwtDecode(data?.session?.access_token) as any;

    //     console.log('jwt', jwt)
    //     if (Array.isArray(jwt?.user_permissions)) {
    //       setPermissions(jwt.user_permissions);
    //     }

    //     if (jwt?.full_name) {
    //       setMeta({
    //         ...meta,
    //         fullName: jwt.full_name,
    //       });
    //     }

    //     setSession(data.session ?? null);
    //     setUser(data.session?.user ?? null);
    //   }

    //   setTimeout(() => {
    //     console.log('lloading!');
    //     setLoading(false);
    //   }, 20)
    // });

    // Listen for auth state changes
    const { data: subscription } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.access_token) {
        const jwt = jwtDecode(session?.access_token) as any;

        if (Array.isArray(jwt?.user_permissions)) {
          setPermissions(jwt.user_permissions);
        }

        if (jwt?.full_name) {
          setMeta({
            ...meta,
            fullName: jwt.full_name,
          });
        }

        setSession(session ?? null);
        setUser(session?.user ?? null);
      }

      setLoading(false);
    });

    return () => {
      subscription?.subscription.unsubscribe();
    };
  }, []);

  const hasPermissions = (
    requiresPermissions?: string[],
    p?: string[] | null,
  ): boolean => {
    return (requiresPermissions ?? []).length < 0 || (
      !!(p ?? permissions ?? []).find((p) =>
        requiresPermissions?.concat(["Administrator"]).includes(p)
      )
    );
  }

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error?.message ?? null };
  };

  const signUp = async (email: string, password: string, fullName?: string) => {
    const { error } = await supabase.auth.signUp({ email, password, options: {
      emailRedirectTo: `${window.location.origin}/login`,
      data: {
        full_name: fullName ?? '',
      }
    } });
    return { error: error?.message ?? null };
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch (e) {
      console.log(e);
    }
    clearUserSession();
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error: error?.message ?? null };
  };

  const value: AuthContextProps = {
    user,
    meta,
    permissions,
    hasPermissions,
    session,
    loading,
    signIn,
    signUp,
    signOut,
    resetPassword,
    clearUserSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
